/* eslint-disable react/jsx-no-target-blank */
import Image from 'next/image';
import Link from 'next/link';
import { Navigation, Pagination } from 'swiper/modules';

import Carousel, { SwiperSlide } from '@components/Carousel/Carousel';
import IsInViewPort from '@components/ui/IsInViewPort';
import STATIC_ROUTES from '@data/staticRoutes.json';
import { buildHref } from '@utils/buildHref/buildHref';

import { BannerWrapper } from './BannerStyle';

export interface BannerItem {
  id?: string;
  url?: string;
  images: Array<{
    url?: string;
    alt?: string;
  }>;
}

export interface BannerProps {
  items: BannerItem[];
  imagesWidth?: number;
  imagesHeight?: number;
  logViewImpression?: boolean;
  pathPrefix: 'hotel' | 'tour';
}

const Banner = ({
  items,
  imagesWidth = 960,
  imagesHeight = 278,
  logViewImpression = false,
  pathPrefix,
}: BannerProps) => {
  if (!items || items.length === 0) {
    return null;
  }

  const hasMultipleItems = items.length > 1;

  const viewPromotionEvent = (index: number) => {
    if (logViewImpression && typeof window !== 'undefined') {
      // GTM event for banner view
      window.dataLayer?.push({
        event: 'promotionView',
        ecommerce: {
          promoView: {
            promotions: [
              {
                id: `banner-${index}`,
                name: 'Otel Listeleme - Banner',
                creative: `Banner ${index}`,
                position: index.toString(),
              },
            ],
          },
        },
      });
    }
  };

  const selectPromotionEvent = (index: number) => {
    if (logViewImpression && typeof window !== 'undefined') {
      // GTM event for banner click
      window.dataLayer?.push({
        event: 'promotionClick',
        ecommerce: {
          promoClick: {
            promotions: [
              {
                id: `banner-${index}`,
                name: 'Otel Listeleme - Banner',
                creative: `Banner ${index}`,
                position: index.toString(),
              },
            ],
          },
        },
      });
    }
  };

  return (
    <BannerWrapper>
      <Carousel
        modules={[Navigation, Pagination]}
        pagination={
          hasMultipleItems
            ? {
                clickable: true,
                dynamicBullets: false,
              }
            : false
        }
        navigation={hasMultipleItems}
        loop={hasMultipleItems}
        slidesPerView={1}
        spaceBetween={10}
      >
        {items?.map((item, index) => {
          const imageUrl =
            item.images?.[0]?.url || '/static/images/setur-static.jpg';
          const imageAlt =
            item.images?.[0]?.alt || 'Setur Görseli Hazırlanmakta';
          const href = buildHref(
            pathPrefix === 'hotel'
              ? STATIC_ROUTES.hotelPathPrefix
              : STATIC_ROUTES.tourPathPrefix,
            item?.url
          );

          return (
            <SwiperSlide key={item.id || index}>
              <IsInViewPort
                key={item.id}
                onViewVisible={() => viewPromotionEvent(index)}
              >
                <Link
                  href={href}
                  target='_blank'
                  title={imageAlt}
                  prefetch={false}
                  onClick={() => selectPromotionEvent(index)}
                >
                  {index > 0 ? (
                    <Image
                      src={imageUrl}
                      alt={imageAlt}
                      width={imagesWidth}
                      height={imagesHeight}
                      draggable={false}
                      style={{ objectFit: 'cover' }}
                      loading='lazy'
                    />
                  ) : (
                    <Image
                      priority
                      src={imageUrl}
                      alt={imageAlt}
                      width={imagesWidth}
                      height={imagesHeight}
                      draggable={false}
                      style={{ objectFit: 'cover' }}
                    />
                  )}
                </Link>
              </IsInViewPort>
            </SwiperSlide>
          );
        })}
      </Carousel>
    </BannerWrapper>
  );
};

export default Banner;
